import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";
import "./WebDescriptionEditor.css";

/**
 * WYSIWYG editor for the public website description.
 *
 * Staff write naturally with a toolbar (bold, headings, lists) and never see
 * markdown, but the value stored in `web_description` is Markdown so the website
 * (react-markdown) renders it directly. Controlled by `value` / `onChange`.
 */
export function WebDescriptionEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (markdown: string) => void;
}) {
  // Tracks the last markdown we emitted, so syncing an external `value` change
  // does not fight the user's own keystrokes.
  const lastEmitted = useRef<string>(value ?? "");

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Markdown],
    content: value ?? "",
    contentType: "markdown",
    editorProps: {
      attributes: {
        class: "web-editor__surface",
        "aria-label": "Website description",
      },
    },
    onUpdate: ({ editor }) => {
      const md = editor.getMarkdown();
      lastEmitted.current = md;
      onChange(md);
    },
  });

  // Push external value changes (e.g. opening an existing project) into the
  // editor without clobbering in-progress edits.
  useEffect(() => {
    if (!editor) return;
    const next = value ?? "";
    if (next !== lastEmitted.current) {
      lastEmitted.current = next;
      editor.commands.setContent(next, { contentType: "markdown" });
    }
  }, [value, editor]);

  if (!editor) return null;

  const btn = (active: boolean) => `web-editor__btn${active ? " is-active" : ""}`;

  return (
    <div className="web-editor">
      <div className="web-editor__toolbar">
        <button type="button" title="Bold" className={btn(editor.isActive("bold"))} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={16} />
        </button>
        <button type="button" title="Italic" className={btn(editor.isActive("italic"))} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={16} />
        </button>
        <button type="button" title="Heading" className={btn(editor.isActive("heading", { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 size={16} />
        </button>
        <button type="button" title="Subheading" className={btn(editor.isActive("heading", { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 size={16} />
        </button>
        <button type="button" title="Bullet list" className={btn(editor.isActive("bulletList"))} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={16} />
        </button>
        <button type="button" title="Numbered list" className={btn(editor.isActive("orderedList"))} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={16} />
        </button>
        <button type="button" title="Quote" className={btn(editor.isActive("blockquote"))} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote size={16} />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
